// @ts-strict-ignore
import React, {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { IconButton, Tooltip, Menu, MenuListProps, Button, ButtonProps } from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import ResponsiveButton from 'components/ResponsiveButton'

interface IMoreButtonMenuProps extends Omit<ButtonProps, 'onClick'> {
  responsive?: boolean
  tooltip?: string
  disabled?: boolean
  label?: any
  icon?: any
  color?: 'primary' | 'secondary' | 'error'
  fullWidth?: boolean
  style?: CSSProperties
  className?: string
  iconOnly?: boolean
  children?: any
  component?: any
  MenuListProps?: MenuListProps
}

function MoreButtonMenu(props: IMoreButtonMenuProps, ref: ForwardedRef<HTMLButtonElement>) {
  const [isOpen, setOpen] = useState(false)

  const [anchorEl, setAnchorEl] = useState(null)

  const {
    responsive,
    tooltip,
    disabled,
    label,
    icon,
    color,
    fullWidth,
    style,
    className,
    component,
    iconOnly,
    children,
    size,
    MenuListProps: menuListProps,
    ...restProps
  } = props
  const buttonProps = useMemo(() => ({
    disabled,
    color,
    fullWidth,
    style,
    className,
    iconOnly,
    size,
    'data-cy': restProps['data-cy'],
    'data-testid': restProps['data-testid'],
  }), [disabled, color, fullWidth, style, className, iconOnly, size, restProps['data-cy'], restProps['data-testid']])
  const iconProp = icon || <MoreHorizIcon />

  const handleButtonClick = useCallback((e) => {
    e.stopPropagation() // needed when this component is used inside clickable elements
    setAnchorEl(e.currentTarget)
    setOpen(true)
  }, [setOpen, setAnchorEl])

  const handleMenuClose = useCallback(() => {
    setOpen(false)
    setAnchorEl(null)
  }, [setOpen, setAnchorEl])

  let buttonElement = null

  if (component) {
    let Component = component

    buttonElement = (
      <Component ref={ref} onClick={handleButtonClick} {...buttonProps}>
        {icon}{label}
      </Component>
    )
  } else if (responsive) {
    buttonElement = (
      <ResponsiveButton
        ref={ref}
        onClick={handleButtonClick}
        label={label}
        icon={iconProp}
        tooltip={tooltip}
        {...buttonProps}
      />
    )
  } else if (label) {
    buttonElement = (
      <Button ref={ref} onClick={handleButtonClick} startIcon={icon} {...buttonProps}>
        {label}
      </Button>
    )
  } else if (tooltip && !disabled) {
    buttonElement = (
      <Tooltip title={tooltip}>
        <IconButton ref={ref} onClick={handleButtonClick} {...buttonProps}>
          {iconProp}
        </IconButton>
      </Tooltip>
    )
  } else {
    buttonElement = (
      <IconButton ref={ref} onClick={handleButtonClick} {...buttonProps}>
        {iconProp}
      </IconButton>
    )
  }

  return (
    <React.Fragment>
      {buttonElement}

      <Menu
        open={isOpen}
        anchorEl={anchorEl}
        onMouseDown={e => e.stopPropagation()}
        onMouseUp={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        onClose={handleMenuClose}
        onClickCapture={handleMenuClose}
        disableAutoFocusItem
        keepMounted
        MenuListProps={menuListProps}
      >
        {children}
      </Menu>
    </React.Fragment>
  )
}

export default memo(forwardRef(MoreButtonMenu))
